import React, { useState } from 'react';
import { CheckCircle, Circle, Volume2 } from 'lucide-react';
import { fetchAudio } from '../../services/api';
import ExerciseCard from '../shared/ExerciseCard';
import TranslatableText from '../shared/TranslatableText';

const SpeakingPanel = ({
  questions,
  userAnswers,
  onMarkAsAnswered,
  onRemoveAnswer,
  onSubmit,
  allAnswered,
  topic,
}) => {
  const [playingId, setPlayingId] = useState(null);

  const playAudio = async (questionId, hintText) => {
    try {
      setPlayingId(questionId);

      // Use the new authenticated audio fetch function
      const audioBlob = await fetchAudio(hintText);

      const blobUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(blobUrl);

      audio.onended = () => {
        setPlayingId(null);
        URL.revokeObjectURL(blobUrl);
      };

      audio.play().catch((err) => {
        console.error('Error playing audio:', err);
        setPlayingId(null);
        URL.revokeObjectURL(blobUrl);
      });
    } catch (error) {
      console.error('Error:', error);
      setPlayingId(null);
    }
  };

  return (
    <ExerciseCard>
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-xl font-semibold text-gray-800">
          Odpověz na otázky pomalu a nahlas
        </h2>
        <div className="text-sm font-medium text-gray-500">
          {Object.keys(userAnswers).length} / {questions.length}
        </div>
      </div>

      {/* Questions list */}
      <div className="flex flex-col gap-4 mb-6">
        {questions.map((question, index) => {
          const isAnswered = userAnswers[question.id];

          return (
            <div
              key={question.id}
              className={`p-4 rounded-lg transition-all ${
                isAnswered
                  ? 'bg-green-50 ring-2 ring-green-200'
                  : 'bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Status icon */}
                <div className="mt-1">
                  {isAnswered ? (
                    <CheckCircle size={24} className="text-green-500" strokeWidth={2} />
                  ) : (
                    <Circle size={24} className="text-gray-400" strokeWidth={2} />
                  )}
                </div>

                {/* Question content */}
                <div className="flex-1">
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-gray-500 text-sm">Otázka:</span>
                      <TranslatableText text={question.question} textColor="#1f2937" topic={topic} />
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-gray-500 text-sm">Řekněte nahlas:</span>
                      <TranslatableText text={question.hint} textColor="#1f2937" topic={topic} />
                      <button
                        onClick={() => playAudio(question.id, question.hint)}
                        disabled={playingId !== null}
                        className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
                          playingId === question.id
                            ? 'bg-amber-500 text-white'
                            : playingId !== null
                              ? 'bg-cyan-400 text-white opacity-60 cursor-not-allowed'
                              : 'bg-cyan-500 hover:bg-cyan-600 text-white cursor-pointer'
                        }`}
                      >
                        <Volume2 size={16} />
                        {playingId === question.id ? 'Přehrává se...' : 'Poslechnout'}
                      </button>
                    </div>
                  </div>

                  {/* Action button */}
                  <button
                    onClick={() => isAnswered ? onRemoveAnswer(question.id) : onMarkAsAnswered(question.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      isAnswered
                        ? 'bg-green-500 hover:bg-green-600 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    {isAnswered ? '✓ Otázka zodpovězena' : 'Odpověděl jsem na otázku'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Submit button */}
      <div className="flex gap-3 justify-center">
        <button
          onClick={onSubmit}
          disabled={!allAnswered}
          className={`px-6 py-3 rounded-lg font-medium transition-all ${
            allAnswered
              ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          Odevzdat odpovědi a návrat do menu
        </button>
      </div>

      {!allAnswered && (
        <div className="mt-4 text-center text-sm text-gray-500">
          Odpovězte na všechny otázky před odevzdáním
        </div>
      )}
    </ExerciseCard>
  );
};

export default SpeakingPanel;
